/**
 * Schoorsteenveger Queries
 * Dunne wrappers rond de backend-API (/api/schoorsteenveger/*) met een
 * Bearer-JWT. De aggregatie (employee/service breakdown, trends, etc.)
 * gebeurt server-side. Export-namen en return-shapes zijn behouden.
 */

import { apiGet } from './apiClient';

// Bouw de query-params op uit de filters (period | startDate+endDate).
const buildParams = (filters = {}) => {
  const params = {};
  if (filters.period) params.period = filters.period;
  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;
  return params;
};

// Get current month revenue -> { count, totalRevenue }
export const getCurrentMonthRevenue = async (filters = {}) => {
  try {
    const res = await apiGet('/schoorsteenveger/revenue', buildParams(filters));
    return { count: res.count || 0, totalRevenue: res.totalRevenue || 0 };
  } catch (error) {
    console.error('Error fetching current month revenue:', error);
    throw error;
  }
};

// Get current month invoice count -> number
export const getCurrentMonthInvoiceCount = async (filters = {}) => {
  try {
    const res = await apiGet('/schoorsteenveger/invoice-count', buildParams(filters));
    return res.count || 0;
  } catch (error) {
    console.error('Error fetching invoice count:', error);
    throw error;
  }
};

// Get revenue per employee -> [...]
export const getRevenuePerEmployee = async (filters = {}) => {
  try {
    return await apiGet('/schoorsteenveger/revenue-per-employee', buildParams(filters));
  } catch (error) {
    console.error('Error fetching revenue per employee:', error);
    throw error;
  }
};

// Get revenue per service -> [...]
export const getRevenuePerService = async (filters = {}) => {
  try {
    return await apiGet('/schoorsteenveger/revenue-per-service', buildParams(filters));
  } catch (error) {
    console.error('Error fetching revenue per service:', error);
    throw error;
  }
};

// Get outstanding invoices -> [...]
export const getOutstandingInvoices = async (filters = {}) => {
  try {
    return await apiGet('/schoorsteenveger/outstanding', buildParams(filters));
  } catch (error) {
    console.error('Error fetching outstanding invoices:', error);
    throw error;
  }
};

// Get monthly revenue trend -> [...]
export const getMonthlyRevenueTrend = async (filters = {}) => {
  try {
    return await apiGet('/schoorsteenveger/monthly-trend', buildParams(filters));
  } catch (error) {
    console.error('Error fetching monthly revenue trend:', error);
    throw error;
  }
};

// Get all dashboard data (server aggregeert in één call)
export const getAllDashboardData = async (filters = {}) => {
  try {
    return await apiGet('/schoorsteenveger/all', buildParams(filters));
  } catch (error) {
    console.error('Error fetching all dashboard data:', error);
    throw error;
  }
};

// RouteLogic: routes vandaag + prognose vooruit (periode-onafhankelijk,
// server cachet 10 min; zie /api/schoorsteenveger/routes)
export const getRoutesData = async () => {
  try {
    return await apiGet('/schoorsteenveger/routes');
  } catch (error) {
    console.error('Error fetching routes:', error);
    return null; // aanvullend; dashboard blijft werken zonder
  }
};

// TrackJack: live posities van de bussen (server cachet 30s).
// { connected:false } zolang de OAuth-koppeling er niet is.
export const getLiveLocations = async () => {
  try {
    return await apiGet('/schoorsteenveger/live-locations');
  } catch (error) {
    console.error('Error fetching live locations:', error);
    return { connected: false, positions: [] };
  }
};

// TrackJack: realisatie — gepland vs gereden per dag + dag-tijdlijn +
// vandaag-stats voor de kaart-legenda (server cachet 10 min).
export const getRealisatie = async () => {
  try {
    return await apiGet('/schoorsteenveger/realisatie');
  } catch (error) {
    console.error('Error fetching realisatie:', error);
    return { connected: false };
  }
};

// TrackJack: gereden spoor van vandaag (kaart-toggle; lazy, server cachet 5 min).
export const getDrivenTracks = async () => {
  try {
    return await apiGet('/schoorsteenveger/driven-tracks');
  } catch (error) {
    console.error('Error fetching driven tracks:', error);
    return { connected: false, tracks: [] };
  }
};

// Pipeline-analyse (hele historie): aanvragen/dag, maandconversie, stages.
// Lazy geladen door de inklapbare sectie onderaan.
export const getPipelineAnalytics = async () => {
  try {
    return await apiGet('/schoorsteenveger/pipeline-analytics');
  } catch (error) {
    console.error('Error fetching pipeline analytics:', error);
    return null;
  }
};

// Funnel: sessies -> aanvragen -> ingepland -> uitgevoerd -> omzet
// (cohort van opportunities aangemaakt in de periode; zie server /funnel)
export const getFunnelData = async (filters = {}) => {
  try {
    return await apiGet('/schoorsteenveger/funnel', buildParams(filters));
  } catch (error) {
    console.error('Error fetching schoorsteenveger funnel:', error);
    return null; // funnel is aanvullend; dashboard blijft werken zonder
  }
};

// Provincie-verdeling: aanvragen/facturen/conversie/omzet/openstaand + diensten
// per provincie (zie server /provincie). Provincie uit GHL-adres (PC4).
export const getProvincieStats = async (filters = {}) => {
  try {
    return await apiGet('/schoorsteenveger/provincie', buildParams(filters));
  } catch (error) {
    console.error('Error fetching provincie stats:', error);
    return null; // aanvullend; dashboard blijft werken zonder
  }
};
